/** Duck (https://github.com/erikras/ducks-modular-redux) for handling general parts of script flow navigation.
    Handlers are set up for each step that are implemented elsewhere with specific business logic. This uses inversion
    of control pattern to avoid the common step navigation functionality from being coupled to specific flows and
    business logic.

    Since it only makes sense to show one script flow at a time in UI, the store holds state for a single
    active script flow.

    Anti-pattern: DON'T add logic for specific flows here. The code should be general to all script flows.
*/

import { push } from 'react-router-redux';

// constants
import { nav } from 'constants/scriptFlow';
import { managementZonesTypes } from 'constants/scripts';

// script flow definitions
import { 
  defaultScriptFlowDefinition, 
  previousScriptFlowDefinition, 
  generateZonesFlowDefinition 
} from 'manual/scriptFlowDefinitionV2';

// selectors
import { getStepNumber, getStepNumberForDirection, getStepAction } from './selectors';
import { getSelectedManagementZonesType } from 'manual/selectors';

// actions
import { logError } from 'api/errors';

// Action Types
export const typePrefix = 'vra/script-flow/';
export const actions = [
  'SET_DEFINITION',
  'SET_ACTIVE_STEP',
  'SET_MANUAL_STEP_ACTIONS',
  'RESET_SCRIPT_FLOW'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});

// Reducer

const INITIAL_STATE = {
  stepActions: [],
  activeStep: {
    stepNumber: 0,
    titleTextMessageKey: null
  }
};

export default function reducer(
  state = INITIAL_STATE,
  action = {}
) {
  switch (action.type) {
    case actions.SET_DEFINITION:
      return {
        ...INITIAL_STATE,
        stepActions: action.payload.definition.stepActions
      };

    case actions.SET_ACTIVE_STEP:
      return {
        ...state,
        activeStep: {
          stepNumber: action.payload.newStepNumber,
          titleTextMessageKey: action.payload.titleTextMessageKey
        }
      };

    case actions.SET_MANUAL_STEP_ACTIONS:
      return {
        ...state,
        stepActions: action.payload.stepActions
      };

    case actions.RESET_SCRIPT_FLOW:
      return INITIAL_STATE;

    default:
      return state;
  }
}

// Actions

const _goStep = ({ dispatch, getState, newStepNumber }) => {
  const state = getState();
  const stepNumber = getStepNumber(state);
  const stepAction = getStepAction(state, newStepNumber);
  if (!stepAction) {
    logError(`No handler defined for step #${stepNumber}!`);
    return;
  }

  const response = stepAction(dispatch, getState);
  if (!response) { return; } // Handler signalled navigation should be canceled.

  dispatch({
    type: actions.SET_ACTIVE_STEP,
    payload: {
      newStepNumber,
      titleTextMessageKey: response.titleTextMessageKey
    }
  });

  const newRoute = response.route;
  if (newRoute) { dispatch(push(newRoute)); }
};

const _goDirection = ({ direction }) => {
  return (dispatch, getState) => {
    const newStepNumber = getStepNumberForDirection(getState(), direction);
    if (newStepNumber === null) {
      logError(`Going ${direction} from step #${newStepNumber} is OOB!`);
      return;
    }
    _goStep({ dispatch, getState, newStepNumber });
  };
};

/** Navigate to step following the current one, calling action function for target step. */
export const goForward = () => { return _goDirection({ direction: nav.FORWARD }); };

/** Navigate to step preceding the current one, calling action function for target step. */
export const goBack = () => { return _goDirection({ direction: nav.BACK }); };

/** Start a script flow by setting its step actions, resetting any prior script-flow-related state, and dispatching
 *  action for arrival on initial step. If the initial step action returns a route, a route push will be
 *  performed as well. You can only have one active script flow at a time.
 *
 *  STEP ACTIONS
 *  The first and last step action functions are "exit steps" outside of the script flow proper.
 *  The first element [0] is an action function for the script flow state that precedes step 1. So if
 *  user is on Step 1, and a call is made to goBackward(), the element [0] action function
 *  can do whatever is needed to exit from the script flow. Similarly, the last action function is
 *  called when going next from the last proper step of the script flow. So if your script flow has 3 steps,
 *  there should be 5 step action functions.
 *
 *  Each step action function is called when the user arrives at a step. It will be passed:
 *      dispatch, getState
 *  Step action function can return null to cause navigation to the step to be canceled. Otherwise...
 *    { titleTextMessageKey,  For text to display in step title.
 *      newRoute }            New route corresponding to arrival at the step.
 *  Both titleTextMessageKey and newRoute values are optional, but omitting means that script flow will
 *  not perform related actions of setting step text or route.
 *
 *  @param definition.stepActions Array of functions that will handle steps of the script flow. See previous comments.
 *  @param stepNumber Step to start on. It's action function will be called. Defaults to 1st step.
 */
export const startScriptFlow = ({ definition, stepNumber = 1 }) => {
  return (dispatch, getState) => {
    dispatch({
      type: actions.SET_DEFINITION,
      payload: { definition }
    });
    _goStep({ dispatch, getState, newStepNumber: stepNumber });
  };
};

// Actions for Manual Script Flow
// Script types such as imported scripts and other sets of stepActions should be included as other cases

export const setStepActionsForManualScripts = () => {
  return (dispatch, getState) => {
    const state = getState();
    const selectedManagementZonesType = getSelectedManagementZonesType(state);
    const currentStep = getStepNumber(state);

    if (currentStep === 1) {
      switch(selectedManagementZonesType) {
        case managementZonesTypes.PREVIOUS_PRESCRIPTION:
        case managementZonesTypes.IMPORTED_MAPS_PARENT:
          dispatch({
            type: actions.SET_MANUAL_STEP_ACTIONS,
            payload: { stepActions: previousScriptFlowDefinition } // change previousScriptFlowDefinition name ?
          });
          break;
        case managementZonesTypes.GENERATE_ZONES:
          dispatch({
            type: actions.SET_MANUAL_STEP_ACTIONS,
            payload: { stepActions: generateZonesFlowDefinition }
          });
          break;
        default:
          dispatch({
            type: actions.SET_MANUAL_STEP_ACTIONS,
            payload: { stepActions: defaultScriptFlowDefinition }
          });
          break;
      }
    }
  };
};

export const resetSriptFlow = () => {
  return { type: actions.RESET_SCRIPT_FLOW };
};
